import {
  completeAudioPart,
  finalizeAudioUpload,
  getAudioUploadStatus,
  presignAudioParts,
} from "../../../../../../services/api-documentation";
import { TSession } from "../../../../../../utils/types";
import recordingSessionService from "./recordingSessionService";
import {
  AudioPartPresign,
  RecordingPartRecord,
  RecordingSessionRecord,
} from "../types/recording";

const DEFAULT_BATCH_SIZE = 10;
const DEFAULT_REFILL_THRESHOLD = 3;

const parseEtag = (etagHeader: string | null): string => {
  if (!etagHeader) return "";
  return etagHeader.replace(/\"/g, "");
};

const safeAudioContentType = (blob: Blob): string => {
  if (blob.type) return blob.type;
  return "application/octet-stream";
};

export class RecordingUploadQueue {
  private readonly session: TSession;
  private readonly recordingSession: RecordingSessionRecord;
  private readonly refillThreshold: number;
  private batchSize: number;
  private highestIssuedPart = 0;
  private lastObservedPart = 0;
  private presignedByPart = new Map<number, AudioPartPresign>();
  private uploadInFlight = false;

  constructor(params: {
    session: TSession;
    recordingSession: RecordingSessionRecord;
    batchSize?: number;
    refillThreshold?: number;
  }) {
    this.session = params.session;
    this.recordingSession = params.recordingSession;
    this.batchSize =
      params.batchSize ||
      this.recordingSession.recommendedPresignBatchSize ||
      DEFAULT_BATCH_SIZE;
    this.refillThreshold = params.refillThreshold || DEFAULT_REFILL_THRESHOLD;
    this.highestIssuedPart = Math.max(
      this.recordingSession.nextPartNumber - 1,
      0,
    );
    this.lastObservedPart = Math.max(
      this.recordingSession.nextPartNumber - 1,
      0,
    );
  }

  async initialize(): Promise<void> {
    await this.ensurePresignsFrom(this.recordingSession.nextPartNumber);
  }

  async enqueuePart(partNumber: number): Promise<void> {
    this.lastObservedPart = Math.max(this.lastObservedPart, partNumber);

    // Offline-first: persist locally and defer all remote work until online.
    if (!navigator.onLine) {
      return;
    }

    await this.ensurePresignsForPart(partNumber);
    await this.maybeRefillPresigns();
    await this.flush();
  }

  async flush(): Promise<void> {
    if (this.uploadInFlight || !navigator.onLine) {
      return;
    }

    this.uploadInFlight = true;
    try {
      const pendingParts = await recordingSessionService.listPartsByStatus(
        this.recordingSession.id,
        ["stored", "failed"],
      );

      for (const part of pendingParts) {
        await this.uploadPart(part);
      }
    } finally {
      this.uploadInFlight = false;
    }
  }

  async syncMissingParts(): Promise<boolean> {
    if (!navigator.onLine) {
      return true;
    }

    const status = await getAudioUploadStatus(
      this.session,
      this.recordingSession.audioId,
      this.recordingSession.uploadSessionId,
    );

    if (
      status.upload_session_id &&
      status.upload_session_id !== this.recordingSession.uploadSessionId
    ) {
      return false;
    }

    if (!status.missing_part_numbers.length) {
      return true;
    }

    const response = await presignAudioParts(
      this.session,
      this.recordingSession.audioId,
      {
        upload_session_id: this.recordingSession.uploadSessionId,
        part_numbers: status.missing_part_numbers,
      },
    );

    response.parts.forEach((part) => {
      this.presignedByPart.set(part.part_number, part);
      this.highestIssuedPart = Math.max(
        this.highestIssuedPart,
        part.part_number,
      );
    });

    await this.flush();
    return true;
  }

  async finalize(finalPartNumber: number): Promise<void> {
    if (!navigator.onLine) {
      throw new Error("Cannot finalize while offline.");
    }

    await this.waitForIdle();
    await this.flush();
    await this.waitForIdle();
    await this.syncMissingParts();

    await finalizeAudioUpload(this.session, this.recordingSession.audioId, {
      upload_session_id: this.recordingSession.uploadSessionId,
      final_part_number: finalPartNumber,
    });
  }

  private async uploadPart(part: RecordingPartRecord): Promise<void> {
    if (!navigator.onLine) return;

    const presigned = await this.ensurePresignsForPart(part.partNumber);
    if (!presigned) return;

    await recordingSessionService.updatePart(part.sessionId, part.partNumber, {
      status: "uploading",
      uploadAttempts: part.uploadAttempts + 1,
    });

    try {
      const response = await fetch(presigned.put_url, {
        method: "PUT",
        body: part.blob,
        headers: {
          "Content-Type": safeAudioContentType(part.blob),
        },
      });

      if (!response.ok) {
        throw new Error(`Part upload failed (${response.status})`);
      }

      const etag = parseEtag(response.headers.get("etag"));

      await completeAudioPart(this.session, this.recordingSession.audioId, {
        upload_session_id: this.recordingSession.uploadSessionId,
        part_number: part.partNumber,
        etag,
        size_bytes: part.sizeBytes,
      });

      await recordingSessionService.updatePart(
        part.sessionId,
        part.partNumber,
        {
          status: "uploaded",
          etag,
          lastError: undefined,
        },
      );
      this.presignedByPart.delete(part.partNumber);
      await this.maybeRefillPresigns();
    } catch (error) {
      await recordingSessionService.updatePart(
        part.sessionId,
        part.partNumber,
        {
          status: "failed",
          lastError: error instanceof Error ? error.message : String(error),
        },
      );
    }
  }

  private async ensurePresignsForPart(
    partNumber: number,
  ): Promise<AudioPartPresign | undefined> {
    if (this.presignedByPart.has(partNumber)) {
      return this.presignedByPart.get(partNumber);
    }

    if (partNumber > this.highestIssuedPart) {
      await this.ensurePresignsFrom(this.highestIssuedPart + 1);
    }

    if (!this.presignedByPart.has(partNumber)) {
      const response = await presignAudioParts(
        this.session,
        this.recordingSession.audioId,
        {
          upload_session_id: this.recordingSession.uploadSessionId,
          part_numbers: [partNumber],
        },
      );

      response.parts.forEach((part) => {
        this.presignedByPart.set(part.part_number, part);
        this.highestIssuedPart = Math.max(
          this.highestIssuedPart,
          part.part_number,
        );
      });
    }

    return this.presignedByPart.get(partNumber);
  }

  private async ensurePresignsFrom(startPartNumber: number): Promise<void> {
    if (startPartNumber <= 0) {
      return;
    }

    const response = await presignAudioParts(
      this.session,
      this.recordingSession.audioId,
      {
        upload_session_id: this.recordingSession.uploadSessionId,
        start_part_number: startPartNumber,
        count: this.batchSize,
      },
    );

    response.parts.forEach((part) => {
      this.presignedByPart.set(part.part_number, part);
      this.highestIssuedPart = Math.max(
        this.highestIssuedPart,
        part.part_number,
      );
    });
  }

  private async maybeRefillPresigns(): Promise<void> {
    const remainingIssuedSlots = this.highestIssuedPart - this.lastObservedPart;
    if (remainingIssuedSlots > this.refillThreshold) {
      return;
    }

    await this.ensurePresignsFrom(this.highestIssuedPart + 1);
  }

  private async waitForIdle(): Promise<void> {
    while (this.uploadInFlight) {
      await new Promise((resolve) => setTimeout(resolve, 50));
    }
  }
}

export const recoverChunkedSession = async (
  session: TSession,
  recordingSession: RecordingSessionRecord,
): Promise<void> => {
  const queue = new RecordingUploadQueue({
    session,
    recordingSession,
  });
  const matchesSession = await queue.syncMissingParts();
  if (!matchesSession) {
    return;
  }
  await queue.initialize();
  await queue.flush();

  if (recordingSession.finalPartNumber) {
    await queue.finalize(recordingSession.finalPartNumber);
    await recordingSessionService.updateSession(recordingSession.id, {
      status: "completed",
      lastError: undefined,
    });
  }
};
